import React, { useMemo, useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RosterClient, StudyTeam } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { QuestionCardDemographic } from 'jf/pages/study/question/QuestionCardDemographic';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { useCompleteSelectTeamsStep } from '../selectTeams/useCompleteSelectTeamsStep';
import { useStudyBuilderState } from '../StudyBuilderState';

export const DEFAULT_TEAM_DEMOGRAPHIC_TEXT = 'What is your primary team?';

export const CustomizableDemographic: React.FC = () => {
  const { data: teams } = useClientQuery(RosterClient.getTeams);
  const state = useStudyBuilderState();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [initialSelectedTeams, setInitialSelectedTeams] = useState<StudyTeam[]>([]);
  const selectTeamsUpdater = useCompleteSelectTeamsStep();
  const theme = useDevExTheme();

  const teamOptions = useMemo(
    () =>
      teams
        ?.filter((team) => team.isLeaf && !team.isHidden)
        .map((team) => ({ value: team.ref, label: team.value })),
    [teams]
  );

  return (
    <Flex vertical align="end" gap={theme.variable.spacing.md}>
      {isEditing ? (
        <Flex gap={theme.variable.spacing.sm}>
          <DevExButton
            type="outline"
            onClick={() => {
              // reset teams to init on cancel
              state.update({ studyTeams: initialSelectedTeams });
              setIsEditing(false);
            }}
          >
            Cancel
          </DevExButton>
          <Tooltip
            title={
              state.studyTeams.length === 0
                ? 'You must select at least one team to continue.'
                : undefined
            }
          >
            <DevExButton
              type="primary"
              loading={isSaving}
              disabled={state.studyTeams.length === 0}
              onClick={() => {
                setIsSaving(true);
                selectTeamsUpdater().then(() => {
                  setIsEditing(false);
                  setIsSaving(false);
                });
              }}
            >
              Save
            </DevExButton>
          </Tooltip>
        </Flex>
      ) : (
        <DevExButton
          type="outline"
          icon={<FontAwesomeIcon icon={faPen} />}
          onClick={() => {
            // set teams initial state on edit click
            setInitialSelectedTeams([...state.studyTeams]);
            setIsEditing(true);
          }}
        >
          Edit
        </DevExButton>
      )}

      <QuestionCardDemographic
        subtitle="Demographics"
        title={DEFAULT_TEAM_DEMOGRAPHIC_TEXT}
        placeholder={
          window.dx.user?.company.isEmp
            ? `Default team based on respondent's allocations`
            : 'Select team...'
        }
        options={teamOptions ?? []}
        isEditing={isEditing}
        disableDropdown
      />
    </Flex>
  );
};
